import { noop } from 'lodash-es';
import { useCallback, useEffect, useRef } from 'react';
import { useBeforeUnload, useBlocker, type Location } from 'react-router-dom';

interface UsePromptRR6Options {
  beforeUnload?: boolean;
  handleOnNavigated?: () => void;
  /** When true, skip the confirm dialog for this in-app navigation only (not beforeunload). */
  allowNavigation?: (nextLocation: Location) => boolean;
}

/** Leverage useBlocker() to block the navigation, in a consistent way to pre-RR6 scenarios */
function usePromptRR6(message: string | boolean, options: UsePromptRR6Options = {}) {
  const { beforeUnload = true, handleOnNavigated = noop, allowNavigation } = options;

  const confirmNavigationWithMessage = useCallback(
    (promptMessage: string) => {
      const navigationConfirmed = window.confirm(promptMessage);
      navigationConfirmed && handleOnNavigated();
      return navigationConfirmed;
    },
    [handleOnNavigated]
  );

  const isBlocked = useCallback(
    ({ nextLocation }: { nextLocation: Location }) => {
      if (typeof message !== 'string') {
        handleOnNavigated();
        return false;
      }
      if (allowNavigation?.(nextLocation)) {
        return false;
      }
      return !confirmNavigationWithMessage(message);
    },
    [allowNavigation, confirmNavigationWithMessage, handleOnNavigated, message]
  );
  const blocker = useBlocker(isBlocked);
  const prevState = useRef(blocker.state);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    prevState.current = blocker.state;
  }, [blocker]);

  useBeforeUnload(
    useCallback(
      event => {
        if (beforeUnload && typeof message === 'string') {
          event.preventDefault();
          // While returnValue is deprecated, MDN suggests to keep this to support legacy cases
          // (https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent)
          event.returnValue = message;
        }
      },
      [message, beforeUnload]
    ),
    { capture: true }
  );
}

export interface RR6PromptProps {
  when: boolean;
  message: string;
  handleOnNavigated?: () => void;
  /** Optional exemption for a specific in-app navigation (e.g. Save-As URL replace). */
  allowNavigation?: (nextLocation: Location) => boolean;
}

/** React-Router 6+ backward compatible version of the RR5 Prompt component, which blocks navigation
 * under speciific conditions.
 * @param when - when to block navigation
 * @param message - message to display in the confirmation dialog
 * @param handleOnNavigated - callback to execute when the user navigates
 * @param allowNavigation - when true for nextLocation, skip the confirm for that navigation only
 */
function RR6PromptLazy({ when, message, handleOnNavigated, allowNavigation }: RR6PromptProps) {
  usePromptRR6(when ? message : false, { handleOnNavigated, allowNavigation });
  return null;
}

export default RR6PromptLazy;
